import type { AxiosResponse } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { zervantSettingsQueryKey } from '../constants/zervantSettingsQueryKey'
import { updateZervantSettings } from '../query-api'
import type { ZervantSettings } from '../types/zervantSettings'
import type { UpdateInvoicingSettingsPatch } from './types/updateInvoicingSettingsPatch'
import { getInvoicingSettingsPayload } from './utils/getInvoicingSettingsPayload'

interface UseUpdateInvoicingSettingsProps {
  onError?: (error: unknown) => void
  onSuccess?: (data: AxiosResponse<ZervantSettings>) => void
}

export const useUpdateInvoicingSettings = ({ onError, onSuccess }: UseUpdateInvoicingSettingsProps = {}) => {
  const queryClient = useQueryClient()

  const {
    mutateAsync: update,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    (patch: UpdateInvoicingSettingsPatch) => {
      // Read cache at save time, not from the render that created this callback.
      const cachedResponse = queryClient.getQueryData<AxiosResponse<ZervantSettings>>(zervantSettingsQueryKey)

      if (!cachedResponse?.data) {
        throw new Error('Invoicing settings cache is empty')
      }

      return updateZervantSettings(
        getInvoicingSettingsPayload({
          cachedSettings: cachedResponse.data,
          patch,
        }),
      )
    },
    {
      onError,
      onSuccess: (response) => {
        // PUT returns the same defaults-merged document as GET, so seed instead of invalidate.
        // Do not invalidate this key from a consumer — an in-flight GET can overwrite a fresher seed.
        queryClient.setQueryData(zervantSettingsQueryKey, response)
        onSuccess?.(response)
      },
    },
  )

  return { update, isProcessing, ...rest }
}
